/**
 * useVideoRecorder Hook
 * 
 * Real-time video recording dengan MediaRecorder
 * 
 * @param {Object} refs - Renderer, scene, camera refs
 * @param {Object} options - Recording options
 * @returns {Object} Recording state dan controls
 */

import { useState, useRef, useCallback } from 'react'

export function useVideoRecorder(refs, options = {}) {
  const {
    rendererRef,
    sceneRef,
    cameraRef,
    selectedCameraRef,
    modelRef,
    mixerRef,
  } = refs

  const {
    videoFormat = 'mp4',
    useCustomResolution = false,
    exportWidth = 1920,
    exportHeight = 1080,
    videoFrameRate = 30,
    videoDuration = 5,
  } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [renderingPhase, setRenderingPhase] = useState('')
  const [recordingProgress, setRecordingProgress] = useState(0)
  
  const mediaRecorderRef = useRef(null)
  const recordedChunksRef = useRef([])
  const offlineFramesRef = useRef([])
  const renderCancelledRef = useRef(false)
  const isRenderingRef = useRef(false)

  // Start real-time recording
  const startRecording = useCallback(
    (selectedAnimation, animationSpeed) => {
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return
      if (!selectedAnimation || !mixerRef.current) {
        alert('Please select an animation first.')
        return
      }

      try {
        const THREE = window.THREE_MODULE

        // Get active camera
        let activeCamera = cameraRef.current
        if (
          selectedCameraRef.current !== 'default' &&
          modelRef.current?.userData.cameras
        ) {
          const cameraData = modelRef.current.userData.cameras.find(
            (c) => c.name === selectedCameraRef.current
          )
          if (cameraData && cameraData.camera) {
            activeCamera = cameraData.camera
          }
        }

        // Store original if using custom resolution
        let originalSettings = null
        if (useCustomResolution) {
          originalSettings = {
            width: rendererRef.current.domElement.width,
            height: rendererRef.current.domElement.height,
            aspect: activeCamera.aspect,
            pixelRatio: rendererRef.current.getPixelRatio(),
            camera: activeCamera,
          }

          rendererRef.current.setSize(exportWidth, exportHeight, false)
          rendererRef.current.setPixelRatio(1)
          activeCamera.aspect = exportWidth / exportHeight
          activeCamera.updateProjectionMatrix()

          window._originalRenderSettings = originalSettings
        }

        const canvas = rendererRef.current.domElement
        const stream = canvas.captureStream(30)

        // Determine mime type
        let mimeType = 'video/webm;codecs=vp9'
        let actualFormat = 'webm'

        if (videoFormat === 'mp4') {
          const mp4Types = [
            'video/mp4;codecs=h264',
            'video/mp4;codecs=avc1',
            'video/mp4',
          ]

          for (const type of mp4Types) {
            if (MediaRecorder.isTypeSupported(type)) {
              mimeType = type
              actualFormat = 'mp4'
              break
            }
          }
        }

        if (actualFormat === 'webm' && !MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm;codecs=vp8'
        }

        const bitrate = actualFormat === 'mp4' ? 10000000 : 8000000

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: bitrate,
        })

        mediaRecorderRef.current = mediaRecorder
        recordedChunksRef.current = []

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data)
          }
        }

        mediaRecorder.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, {
            type: actualFormat === 'mp4' ? 'video/mp4' : 'video/webm',
          })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          const resolution = useCustomResolution
            ? `${exportWidth}x${exportHeight}-`
            : ''
          const extension = actualFormat === 'mp4' ? 'mp4' : 'webm'
          link.download = `3d-video-${resolution}${timestamp}.${extension}`
          link.href = url
          link.click()
          URL.revokeObjectURL(url)

          console.log(`Video saved: ${extension.toUpperCase()}`)
          recordedChunksRef.current = []
          setIsRecording(false)

          // Restore settings
          if (window._originalRenderSettings && rendererRef.current) {
            const settings = window._originalRenderSettings
            rendererRef.current.setSize(settings.width, settings.height, false)
            rendererRef.current.setPixelRatio(settings.pixelRatio)
            if (settings.camera) {
              settings.camera.aspect = settings.aspect
              settings.camera.updateProjectionMatrix()
            }
            delete window._originalRenderSettings
          }
        }

        // Get animation
        const animations = modelRef.current?.userData?.animations
        const selectedAnim = animations?.find(
          (anim, idx) =>
            (anim.name || `Animation ${idx + 1}`) === selectedAnimation
        )

        if (!selectedAnim) {
          alert('Animation not found')
          return
        }

        const duration = selectedAnim.duration / animationSpeed

        // Start recording
        mediaRecorder.start()
        setIsRecording(true)

        // Play animation
        mixerRef.current.stopAllAction()
        const action = mixerRef.current.clipAction(selectedAnim)
        action.timeScale = animationSpeed
        action.setLoop(THREE.LoopOnce, 1)
        action.clampWhenFinished = true
        action.reset().play()

        console.log(
          `Recording started - Duration: ${duration.toFixed(2)}s at ${animationSpeed}x`
        )

        // Auto-stop
        setTimeout(() => {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.requestData()
            setTimeout(() => {
              if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop()
                console.log('Recording auto-stopped')
              }
            }, 200)
          }
        }, (duration + 0.5) * 1000)
      } catch (error) {
        console.error('Error starting recording:', error)
        alert('Failed to start recording.')
      }
    },
    [
      rendererRef,
      sceneRef,
      cameraRef,
      selectedCameraRef,
      modelRef,
      mixerRef,
      videoFormat,
      useCustomResolution,
      exportWidth,
      exportHeight,
    ]
  )

  // Stop recording manually
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.requestData()
      }

      setTimeout(() => {
        if (mediaRecorderRef.current?.state !== 'inactive') {
          mediaRecorderRef.current.stop()
          setIsRecording(false)
          console.log('Recording stopped manually')
        }
      }, 100)

      // Restore settings
      if (window._originalRenderSettings && rendererRef.current) {
        const settings = window._originalRenderSettings
        rendererRef.current.setSize(settings.width, settings.height, false)
        rendererRef.current.setPixelRatio(settings.pixelRatio)
        if (settings.camera) {
          settings.camera.aspect = settings.aspect
          settings.camera.updateProjectionMatrix()
        }
        delete window._originalRenderSettings
      }
    }
  }, [rendererRef])

  // High-Quality Offline Rendering (Frame-by-Frame)
  const startHighQualityRecording = useCallback(
    async (selectedAnimation, animationSpeed, cameraKeyframes, interpolateCamera) => {
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return

      const hasModelAnimation = selectedAnimation && mixerRef.current
      const hasCameraAnimation = cameraKeyframes && cameraKeyframes.length >= 2

      if (!hasModelAnimation && !hasCameraAnimation) {
        alert('Please select a model animation or create camera animation keyframes first.')
        return
      }

      try {
        const THREE = window.THREE_MODULE

        setIsRendering(true)
        isRenderingRef.current = true
        setRenderingPhase('capturing')
        setRecordingProgress(0)
        renderCancelledRef.current = false
        offlineFramesRef.current = []

        // Get animation
        let selectedAnim = null
        let animationDurationToUse = videoDuration

        if (hasModelAnimation) {
          const animations = modelRef.current.userData.animations
          selectedAnim = animations.find(
            (anim, idx) => (anim.name || `Animation ${idx + 1}`) === selectedAnimation
          )
          if (selectedAnim) {
            animationDurationToUse = selectedAnim.duration
          }
        }

        // Calculate frames
        const totalFrames = Math.ceil(videoFrameRate * animationDurationToUse)
        const deltaTime = animationDurationToUse / totalFrames

        // Get active camera
        let activeCamera = cameraRef.current
        if (selectedCameraRef.current !== 'default' && modelRef.current?.userData.cameras) {
          const cameraData = modelRef.current.userData.cameras.find(
            (c) => c.name === selectedCameraRef.current
          )
          if (cameraData && cameraData.camera) {
            activeCamera = cameraData.camera
          }
        }

        // Store original settings
        const originalSettings = {
          width: rendererRef.current.domElement.width,
          height: rendererRef.current.domElement.height,
          aspect: activeCamera.aspect,
          pixelRatio: rendererRef.current.getPixelRatio(),
        }

        // Set custom resolution
        rendererRef.current.setSize(exportWidth, exportHeight, false)
        rendererRef.current.setPixelRatio(1)
        activeCamera.aspect = exportWidth / exportHeight
        activeCamera.updateProjectionMatrix()

        // Setup animation mixer if needed
        let mixer = null
        let action = null
        if (hasModelAnimation && selectedAnim) {
          mixer = mixerRef.current
          mixer.stopAllAction()
          action = mixer.clipAction(selectedAnim)
          action.timeScale = 1
          action.setLoop(THREE.LoopOnce, 1)
          action.clampWhenFinished = true
          action.reset()
          action.time = 0
          action.play()
          action.paused = true
        }

        console.log(`Starting high-quality render: ${totalFrames} frames at ${videoFrameRate}fps`)

        // Render frames
        for (let frame = 0; frame < totalFrames; frame++) {
          if (renderCancelledRef.current) {
            console.log('Rendering cancelled by user')
            break
          }

          const currentTime = frame * deltaTime

          // Update model animation
          if (mixer && action) {
            action.time = currentTime * animationSpeed
            mixer.update(0)
          }

          // Update camera animation
          if (hasCameraAnimation && interpolateCamera) {
            interpolateCamera(currentTime)
          }

          // Render frame
          rendererRef.current.render(sceneRef.current, activeCamera)

          // Capture frame
          const dataUrl = rendererRef.current.domElement.toDataURL('image/png')
          offlineFramesRef.current.push(dataUrl)

          // Update progress
          const progress = ((frame + 1) / totalFrames) * 100
          setRecordingProgress(Math.round(progress))

          // Allow UI to update
          await new Promise((resolve) => setTimeout(resolve, 0))
        }

        if (renderCancelledRef.current) {
          setIsRendering(false)
          isRenderingRef.current = false
          setRenderingPhase('')
          setRecordingProgress(0)
          offlineFramesRef.current = []

          // Restore settings
          rendererRef.current.setSize(originalSettings.width, originalSettings.height, false)
          rendererRef.current.setPixelRatio(originalSettings.pixelRatio)
          activeCamera.aspect = originalSettings.aspect
          activeCamera.updateProjectionMatrix()
          return
        }

        // Encode video using FFmpeg or fallback
        setRenderingPhase('encoding')
        console.log('Encoding video frames...')

        // For now, create image sequence download (placeholder for video encoding)
        // In production, you would use FFmpeg.js or server-side encoding
        const zip = await createImageSequenceZip(offlineFramesRef.current)
        downloadZip(zip, `3d-render-${exportWidth}x${exportHeight}-${videoFrameRate}fps.zip`)

        console.log('✅ High-quality render complete!')
        setRenderingPhase('complete')
        setRecordingProgress(100)

        // Cleanup
        setTimeout(() => {
          setIsRendering(false)
          isRenderingRef.current = false
          setRenderingPhase('')
          setRecordingProgress(0)
          offlineFramesRef.current = []
        }, 2000)

        // Restore settings
        rendererRef.current.setSize(originalSettings.width, originalSettings.height, false)
        rendererRef.current.setPixelRatio(originalSettings.pixelRatio)
        activeCamera.aspect = originalSettings.aspect
        activeCamera.updateProjectionMatrix()
      } catch (error) {
        console.error('Error during high-quality rendering:', error)
        alert('Failed to render video. Please try again.')
        setIsRendering(false)
        isRenderingRef.current = false
        setRenderingPhase('')
        setRecordingProgress(0)
      }
    },
    [
      rendererRef,
      sceneRef,
      cameraRef,
      selectedCameraRef,
      modelRef,
      mixerRef,
      exportWidth,
      exportHeight,
      videoFrameRate,
      videoDuration,
    ]
  )

  // Cancel rendering
  const cancelRendering = useCallback(() => {
    renderCancelledRef.current = true
    console.log('Cancelling render...')
  }, [])

  // Helper: Create image sequence ZIP (placeholder for video encoding)
  const createImageSequenceZip = async (frames) => {
    // This is a simplified version - in production use JSZip
    return { frames, count: frames.length }
  }

  // Helper: Download ZIP
  const downloadZip = (zip, filename) => {
    // Simplified - just download first frame as example
    if (zip.frames && zip.frames.length > 0) {
      const link = document.createElement('a')
      link.download = filename.replace('.zip', '-frame-000.png')
      link.href = zip.frames[0]
      link.click()
      alert(`Rendered ${zip.count} frames. Download the first frame as example.\n\nFor full video encoding, integrate FFmpeg.js or server-side encoding.`)
    }
  }

  return {
    isRecording,
    isRendering,
    renderingPhase,
    recordingProgress,
    startRecording,
    stopRecording,
    startHighQualityRecording,
    cancelRendering,
    mediaRecorderRef,
  }
}

